import styles from './EditDiagramModal.module.scss';
import { DrawIoEmbed, DrawIoEmbedRef, EventExport } from 'react-drawio';
import { useRef } from 'react';
import { AdvancedFormReturnType } from 'helpers/form/useAdvancedForm.ts';
import { Dialog } from '@mui/material';
import { TextButton } from 'components/uikit/buttons/TextButton.tsx';
import CloseIcon from 'assets/icons/close-button.svg?react';
import { useModalExternalButton } from 'components/uikit/modal/useModalExternalButton.tsx';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { VariableFormData } from '../variableModal/VariableModal';
import { drawIoServerUrl } from 'application/constants/env-variables.ts';

export type EditDiagramModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  form: AdvancedFormReturnType<VariableFormData>;
  xml?: string | null;
};

export const EditDiagramModal = (props: EditDiagramModalProps) => {
  const i18n = useScopedTranslation('Component.EditDiagramModal');
  const confirmModalButtonsIds = {
    ok: 'okButtonId',
    cancel: 'cancelButtonId',
  };

  const { anchor, buttonRef } = useModalExternalButton({
    offsetRight: -40,
    offsetTop: 0,
    isOpen: props.isOpen,
  });
  const drawIoRef = useRef<DrawIoEmbedRef>(null);
  const modals = useModal();

  const onDrawIoExport = (data: EventExport) => {
    props.form.setValue('actualValue.value', data.data);
    props.form.setValue('actualValue.alternateValue', data.xml);
    props.setIsOpen(false);
  };

  const onClose = async (forceClose: boolean = false) => {
    const result = !forceClose
      ? await modals.showMultiButton({
          title: i18n.t('ConfirmModal.title'),
          text: i18n.t('ConfirmModal.text'),
          buttons: [
            {
              id: confirmModalButtonsIds.cancel,
              text: i18n.t('ConfirmModal.cancelButton'),
              color: ButtonColor.Secondary,
            },
            {
              id: confirmModalButtonsIds.ok,
              text: i18n.t('ConfirmModal.okButton'),
            },
          ],
        })
      : confirmModalButtonsIds.cancel;

    switch (result) {
      case confirmModalButtonsIds.ok:
        if (drawIoRef.current)
          drawIoRef.current.exportDiagram({ format: 'png' });
        break;
      case confirmModalButtonsIds.cancel:
        props.setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpen}
        onClose={() => onClose()}
        maxWidth={'xl'}
        classes={dialogClasses}
      >
        {anchor}
        <TextButton
          ref={buttonRef}
          icon={<CloseIcon />}
          className={styles.closeButton}
          onClick={() => onClose()}
        />
        <DrawIoEmbed
          ref={drawIoRef}
          onExport={onDrawIoExport}
          onClose={() => onClose(true)}
          exportFormat={'png'}
          xml={props.xml ?? undefined}
          baseUrl={drawIoServerUrl()}
        />
      </Dialog>
    </>
  );
};

const dialogClasses = {
  paper: styles.editDiagramModal,
};
